import React from 'react';
import { useCustomCompareEffect } from 'react-use';
import _isEqual from 'lodash/isEqual';
import _noop from 'lodash/noop';
import {
  Guid,
  Dashboard,
  ModuleTypesConfig,
  GeneralTypesConfig,
  DashboardConfig,
  DashboardType,
  GeneralConfigKeys,
  LibraryData,
  LibraryDashboardContent,
} from '../types';
import useLayoutConfigurationState, {
  layoutConfigSelectors,
  LayoutConfigDispatch,
  dashboardStateActions,
} from '../hooks/useLayoutConfigurationState';

// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
const emptyHandler = async (..._args: any[]): Promise<void> => null;

export type UpdateDashboard = (dashboard: Dashboard) => Promise<void>;
export type DeleteDashboard = (dashboardId: Guid) => Promise<void>;
export type PublishDashboard = (
  libraryDashboard: LibraryDashboardContent,
) => Promise<void>;
export type UnpublishDashboard = (libraryDashboardId: Guid) => Promise<void>;
export type ChangeDefaultDashboard = (
  originalDefault: Dashboard,
  newDefault: Dashboard,
) => Promise<void>;
export type CreateDashboard = (
  dashboard: Omit<Dashboard, 'id'>,
) => Promise<void>;

// cache contexts
export const UpdateDashboardContext =
  React.createContext<UpdateDashboard>(emptyHandler);
export const DeleteDashboardContext =
  React.createContext<DeleteDashboard>(emptyHandler);
export const PublishDashboardContext =
  React.createContext<PublishDashboard>(emptyHandler);
export const UnpublishDashboardContext =
  React.createContext<UnpublishDashboard>(emptyHandler);
export const ChangeDefaultDashboardContext =
  React.createContext<ChangeDefaultDashboard>(emptyHandler);
export const CreateDashboardContext =
  React.createContext<CreateDashboard>(emptyHandler);
export const UpdatesDisabledContext = React.createContext<boolean>(true);
export const ModuleTypesConfigContext =
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  React.createContext<ModuleTypesConfig>(null);
export const GeneralTypesConfigContext =
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  React.createContext<GeneralTypesConfig>(null);

// edit state contexts
export const LayoutStateDispatchContext =
  React.createContext<LayoutConfigDispatch>(_noop);
export const IsStaticDefaultLayoutSelectedContext =
  React.createContext<boolean>(false);
export const SelectedDashboardConfigContext =
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  React.createContext<DashboardConfig>(null);
export const SelectedDashboardsConfigContext = React.createContext<{
  left: DashboardConfig | null;
  right: DashboardConfig | null;
}>({ left: null, right: null });
export const HasEmptyLayoutContext = React.createContext<boolean>(false);
export const IsInEditModeContext = React.createContext<boolean>(false);
export const IsInSplitScreenModeContext = React.createContext<boolean>(false);
// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
export const EditModuleKeyContext = React.createContext<Guid>(null);
export const EditSettingsKeyContext =
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  React.createContext<GeneralConfigKeys>(null);
export const IsEditingSettingsContext = React.createContext<boolean>(false);
export const IsEditingModuleSettingsContext =
  React.createContext<boolean>(false);
export const IsEditingNonModuleSettingsContext =
  React.createContext<boolean>(false);
export const IsInLayoutManagerModeContext = React.createContext<boolean>(false);
export const PersistedVersionOfSelectedConfigContext =
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  React.createContext<DashboardConfig>(null);
export const IsLayoutSelectedContext = React.createContext<boolean>(false);
export const IsCachedLayoutsInitializedContext =
  React.createContext<boolean>(false);
// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
export const DashboardTypeContext = React.createContext<DashboardType>(null);
export const CachedDashboardsContext = React.createContext<Dashboard[]>([]);
export const IsRawConfigDialogOpenContext = React.createContext<boolean>(false);
// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
export const SelectedLayoutIdContext = React.createContext<Guid>(null);
export const SelectedLayoutsIdContext = React.createContext<{
  left: null | Guid;
  right: null | Guid;
}>({ left: null, right: null });
export const LibraryDataContext = React.createContext<LibraryData>({
  modules: [],
  dashboards: [],
});
export const ShowAgentChatContext = React.createContext<boolean>(false);

export interface ConfigurableLayoutContextRootOwnProps {
  onUpdateDashboard: UpdateDashboard;
  onCreateDashboard: CreateDashboard;
  onDeleteDashboard: DeleteDashboard;
  onPublishDashboard: PublishDashboard;
  onUnpublishDashboard: UnpublishDashboard;
  onChangeDefaultDashboard: ChangeDefaultDashboard;
  dashboards: Dashboard[];
  updatesDisabled: boolean;
  moduleTypesConfig: ModuleTypesConfig;
  generalTypesConfig: GeneralTypesConfig;
  dashboardFilter?: (dashboard: Dashboard) => boolean;
  dashboardType: DashboardType;
  libraryData: LibraryData;
  children: any;
}

export type ConfigurableLayoutContextRootProps =
  ConfigurableLayoutContextRootOwnProps;

type ContextValuePair<T> = [React.Context<T>, T];
const composeContexts = (contexts: ContextValuePair<any>[], children: any) =>
  contexts.reduce((acc, [Context, value]) => {
    return <Context.Provider value={value}>{acc}</Context.Provider>;
  }, children);

const ConfigurableLayoutContextRoot: React.FC<
  ConfigurableLayoutContextRootProps
> = (props: ConfigurableLayoutContextRootProps) => {
  const [state, dispatch] = useLayoutConfigurationState();
  useCustomCompareEffect(
    () => {
      // on dashboard change
      const safeDashboards = props.dashboards || [];
      const dashboardsToCache = props.dashboardFilter
        ? safeDashboards.filter(props.dashboardFilter)
        : safeDashboards;
      dispatch(dashboardStateActions.setCachedLayouts(dashboardsToCache));
    },
    [props.dashboards, dispatch, props.dashboardFilter],
    ([prevDashboards], [newDashboards]) =>
      _isEqual(prevDashboards, newDashboards),
  );

  const contexts: ContextValuePair<any>[] = [
    [LibraryDataContext, props.libraryData],
    [UpdateDashboardContext, props.onUpdateDashboard],
    [DeleteDashboardContext, props.onDeleteDashboard],
    [PublishDashboardContext, props.onPublishDashboard],
    [UnpublishDashboardContext, props.onUnpublishDashboard],
    [ChangeDefaultDashboardContext, props.onChangeDefaultDashboard],
    [CreateDashboardContext, props.onCreateDashboard],
    [ModuleTypesConfigContext, props.moduleTypesConfig],
    [GeneralTypesConfigContext, props.generalTypesConfig],
    [UpdatesDisabledContext, props.updatesDisabled],
    [LayoutStateDispatchContext, dispatch],
    [IsStaticDefaultLayoutSelectedContext, state.isStaticDefaultLayoutSelected],
    [SelectedDashboardConfigContext, state.selectedLayoutConfig],
    [SelectedDashboardsConfigContext, state.selectedLayoutsConfig],
    [HasEmptyLayoutContext, layoutConfigSelectors.hasEmptyLayout(state)],
    [IsInEditModeContext, state.isInEditMode],
    [IsInSplitScreenModeContext, state.isInSplitScreenMode],
    [EditModuleKeyContext, state.editModuleKey],
    [EditSettingsKeyContext, state.editSettingsKey],
    [IsEditingSettingsContext, layoutConfigSelectors.isEditingSettings(state)],
    [
      IsEditingModuleSettingsContext,
      layoutConfigSelectors.isEditingModuleSettings(state),
    ],
    [
      IsEditingNonModuleSettingsContext,
      layoutConfigSelectors.isEditingNonModuleSettings(state),
    ],
    [IsInLayoutManagerModeContext, state.isInLayoutManagerMode],
    [
      PersistedVersionOfSelectedConfigContext,
      state.persistedVersionOfSelectedConfig,
    ],
    [IsLayoutSelectedContext, layoutConfigSelectors.isLayoutSelected(state)],
    [IsCachedLayoutsInitializedContext, state.cachedLayoutsInitialized],
    [DashboardTypeContext, props.dashboardType],
    [CachedDashboardsContext, state.cachedLayouts],
    [IsRawConfigDialogOpenContext, state.isRawConfigDialogOpen],
    [SelectedLayoutIdContext, state.selectedLayoutId],
    [SelectedLayoutsIdContext, state.selectedLayoutsId],
    [ShowAgentChatContext, state.showAgentChat],
  ];

  return composeContexts(contexts, props.children);
};

export default React.memo(ConfigurableLayoutContextRoot);
